/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode } from "react";
import { ApolloError } from "@apollo/client";
import { If, Then, Else } from "react-if";
import { Message, Button, theme, FlexContainer } from "@nordcloud/gnui";
import { isNotLocalServer } from "~/config";
import { SimpleError } from "~/tools";

type Props = {
  error?: ApolloError | Error | SimpleError;
  status?: "danger" | "notification";
  onClick?: () => void;
};

export function ErrorMessage({ error, status = "danger" }: Props) {
  return error ? (
    <Message
      status={status}
      image="danger"
      css={{
        marginBottom: theme.spacing.spacing04,
      }}
    >
      <FlexContainer align-items="space-between">
        <p>
          <If condition={isNotLocalServer}>
            <Then>
              Sorry, something went wrong. Please try again or contact us using
              products@nordcloud.com.
            </Then>
            <Else>{error.message}</Else>
          </If>
        </p>
      </FlexContainer>
    </Message>
  ) : null;
}

export function ErrorMessageWithRecover({
  onClick = () => undefined,
  ...props
}: Props) {
  return (
    <FlexContainer justifyContent="center" direction="column">
      <div css={{ width: "100%" }}>
        <ErrorMessage {...props} />
      </div>
      <Button marginTop={theme.spacing.spacing03} onClick={onClick}>
        Reload
      </Button>
    </FlexContainer>
  );
}

export function UserErrorMessage({
  error,
  status = "notification",
  onClick,
}: Pick<Props, "onClick" | "status"> & { error?: Error }) {
  return error ? (
    <Message status={status} image="danger">
      <FlexContainer justifyContent="space-between" css={{ width: "100%" }}>
        <div>{error.message}</div>
        <Button icon="close" size="sm" color={status} onClick={onClick} />
      </FlexContainer>
    </Message>
  ) : null;
}

export function UserErrorMessageHTML({
  html,
  status = "notification",
  onClick,
}: Pick<Props, "onClick" | "status"> & { html?: ReactNode }) {
  return html ? (
    <Message status={status} image="danger">
      <FlexContainer justifyContent="space-between" css={{ width: "100%" }}>
        <div>{html}</div>
        <Button icon="close" size="sm" color={status} onClick={onClick} />
      </FlexContainer>
    </Message>
  ) : null;
}
